import * as React from 'react';
// Import material ui modules
import {
	ArrowForward,
	ArrowBack,
	Check,
} from '@mui/icons-material';
// Import hooks.
import {
	useInputDisqualifiersText,
	UseInputDisqualifiersTextResults,
	UseInputFormResult,
} from '@app/hooks/view/input';
// Import props.
import {
	FormPopupConfirmationProps,
	FormProps,
	NavigationFooterProps,
	FormDisqualifiersProps,
	ScrollbarProps,
	StatusProps,
} from '@app/props';
// Import enumerations.
import {
	AppThemeColor,
	NavigationFooterAlignment,
} from '@app/enumerations';
// Import api models.
import {
    DisqualifiersText,
	DisqualifyingConditionsAgreement,
} from '@app/models/api';

interface UseFormDisqualifiersParameters {
	id: string;
	props: FormDisqualifiersProps,
	showValidations?: boolean;
}

interface UseFormDisqualifiersResults {
	submitForm: () => void;
	submitFormOnAnswerSelect: (selectedDisqualifierAnswer: DisqualifyingConditionsAgreement) => void
	disqualifiersTextInput: UseInputDisqualifiersTextResults;
	applyFormProps: () => FormProps & ScrollbarProps;
	applyNavigationFooterProps: () => NavigationFooterProps;
	applySubmissionFormPopupConfirmationProps: () => FormPopupConfirmationProps;
}

const useFormDisqualifiers = (params: UseFormDisqualifiersParameters): UseFormDisqualifiersResults => {

	// #region exported methods

	/**
	 *  Starts the submission process with validation for the address.
	 **/
	const submitForm = (): void => {
		// First flag all inputs to show any validation error.s
		flagAllInputsToShowValidations();
		// Validate the whole form.
		let isValid: boolean = checkFormValidation(true);
		// Make sure the confirmation popup appears if it's required.
		if (isValid && disqualifiersTextInput.value !== undefined) {
			if (requireConfirmationOnSave) {
				setOpenSaveConfirmationPopup(true);
			}
			else {
				params.props.onSaveDisqualifiersAnswer(disqualifiersTextInput.value);
			}			
		}
	}

	/**
	 *  Starts the submission process for the address.
	 **/
	const submitFormOnAnswerSelect = (selectedDisqualifierAnswer: DisqualifyingConditionsAgreement): void => {
		params.props.onSaveDisqualifiersAnswer(selectedDisqualifierAnswer);
	}

	/**
	 * Returns props for form. 
	 **/
	const applyFormProps = (): FormProps & ScrollbarProps => {
		return {
			id: generateIdForForm(),
			title: 'Appetite and Eligibility',
			themeType: AppThemeColor.PRIMARY,
			fixedNavigation: false,
			statusProps: applyStatusProps(),
		};
	};

	/**
	 * Returns props for the navigation footer. 
	 **/
	const applyNavigationFooterProps = (): NavigationFooterProps => {
		let isSaveRequired: boolean = doesFormRequireSave();
		return {
			id: generateIdForNavigationFooter(),
			leftButtonDisabled: false,
			leftButtonLabel: 'Back',
			leftButtonClick: params.props.onBack,
			leftButtonIcon: ArrowBack,
			rightButtonDisabled: false,
			rightButtonLabel: isSaveRequired ? 'Save' : 'Continue',
			rightButtonClick: isSaveRequired ? submitForm : params.props.onForward,
			rightButtonIcon: isSaveRequired ? Check : ArrowForward,
			alignment: NavigationFooterAlignment.SPACE_BETWEEN,
		};
	};

	/**
	 * Returns props for the submission confirmation popup form. 
	 **/
	const applySubmissionFormPopupConfirmationProps = (): FormPopupConfirmationProps => {
		return {
			isOpen: openSaveConfirmationPopup,
			onClose: cancelConfirmSubmission,
			onConfirm: confirmSubmission,
			message: params.props.confirmationSaveMessage ?? String.empty(),
		};
	};

	// #endregion exported methods


	// #region private methods

	/**
	 * Returns props for the status. 
	 **/
	const applyStatusProps = (): StatusProps | undefined => {
		let statusProps: StatusProps | undefined = undefined;
		if (showStatusMessage) {
			statusProps = {
				statusMessage: params.props.statusMessage ?? String.empty(),
				statusType: AppThemeColor.ERROR,
			}
		}

		return statusProps;
	}

	/**
	 * Confirms submission if user chooses to. 
	 **/
	const confirmSubmission = (): void => {
		setOpenSaveConfirmationPopup(false);

		if (disqualifiersTextInput.value !== undefined) {
			params.props.onSaveDisqualifiersAnswer(disqualifiersTextInput.value);
		}
	}

	/**
	 * Cancels the confirmation prompt for submission. 
	 **/
	const cancelConfirmSubmission = (): void => {
		setOpenSaveConfirmationPopup(false);
	}

	/**
	 * Validates an individual input.
	 * @param input
	 */
	const checkInputValidation = <T>(input: UseInputFormResult<T>, focusToElement: boolean): boolean => {
		let isValid = input.isInputValid;
		if (!isValid && focusToElement) {
			input.focusToInput();
		}
		return isValid;
	}

	/**
	 * Method to check form validation. 
	 **/
	const checkFormValidation = (focusToElement: boolean): boolean => {
		return checkInputValidation<DisqualifyingConditionsAgreement | undefined>(disqualifiersTextInput, focusToElement);
	}

	/**
	 * Flags all inputs to show the validation messages. 
	 **/
	const flagAllInputsToShowValidations = (): void => {
		disqualifiersTextInput.changeShowValidationFlag(true);
	}

	/**
	 * Checks to see if the form has changes. 
	 **/
	const doesFormHaveChanges = (): boolean => {
		return disqualifiersTextInput.doesInputHaveChanges;
	}

	/**
	 * Generates id for the form. 
	 **/
	const generateIdForForm = (): string => {
		return params.id;
	}

	/**
	 * Generates id for the disqualifiers text. 
	 **/
	const generateIdForDisqualifiersText = (): string => {
		return `${params.id}-disqualifiersText`;
	}

	/**
	 * Generates id for the navigation footer. 
	 **/
	const generateIdForNavigationFooter = (): string => {
		return `${generateIdForForm()}-navigationFooter`;
	}

	/**
	 * Checks to see if the form requires saving. 
	 **/
	const doesFormRequireSave = (): boolean => {
		return params.props.allowEdits
			&& !params.props.saveOnAnswerSelect
			&& (doesFormHaveChanges()
				|| params.props.isReviewRequired);
	}

	/**
	 * Fetches the initial disqualifier answer. Current answer is removed if it's version does not match the text version (only when the form is enabled).
	 * @param text
	 **/
	const getInitialDisqualifierAnswer = (text: DisqualifiersText): DisqualifyingConditionsAgreement | undefined => {
		let answer: DisqualifyingConditionsAgreement | undefined = params.props.disqualifiersAnswer;

		if (params.props.allowEdits
			&& text.version != null
			&& answer !== undefined
			&& answer.version != text.version) {
			answer = undefined;
		}
		return answer;
	}

	// #endregion private methods


	// #region private variables

	let requireConfirmationOnSave: boolean = params.props.requireConfirmationOnSave ?? false;

	// Setup the base form. 
	const disqualifiersTextInput: UseInputDisqualifiersTextResults = useInputDisqualifiersText({
		id: generateIdForDisqualifiersText(),
		label: '',
		required: true,
		disabled: !params.props.allowEdits,
		disqualifiersText: params.props.disqualifiersText,
		disqualifiersAnswer: getInitialDisqualifierAnswer(params.props.disqualifiersText),
		onSelectDisqualifiersAnswer: params.props.saveOnAnswerSelect ? submitFormOnAnswerSelect : undefined,
		showValidation: params.showValidations,
	});

	const [openSaveConfirmationPopup, setOpenSaveConfirmationPopup] = React.useState<boolean>(false)
	const [showStatusMessage, setShowStatusMessage] = React.useState<boolean>(false);

	// #endregion private variables


	// #region life-cycle methods.

	// Called when the hook is initially mounted, and returns a call back that will be called on unmounting.
	React.useEffect(() => {
		// If validations are to be shown, then initially scroll to the first invalidated input.
		if (!params.showValidations
			|| checkFormValidation(true)) {
			// If validations pass or are not to be shown, then initially the first input should be focused.
			disqualifiersTextInput.focusToInput();
		}
	}, []);

	// Called when updates are made the props related to the status of the form.
	React.useEffect(() => {
		// Determine if the status message should be rendered.
		if (params.props.isReviewRequired
			&& params.props.statusMessage !== undefined
			&& params.props.allowEdits
			&& checkFormValidation(false)) {
			setShowStatusMessage((currentShowStatusMessage: boolean) => {
				return true;
			});
		}
		else {
			setShowStatusMessage((currentShowStatusMessage: boolean) => {
				return false;
			});
		}
	}, [params.props.isReviewRequired, params.props.statusMessage, params.props.allowEdits]);

	// #endregion life-cycle methods.


	return {
		submitForm: submitForm,
		submitFormOnAnswerSelect: submitFormOnAnswerSelect,
		disqualifiersTextInput: disqualifiersTextInput,
		applyFormProps: applyFormProps,
		applyNavigationFooterProps: applyNavigationFooterProps,
		applySubmissionFormPopupConfirmationProps: applySubmissionFormPopupConfirmationProps,
	};
}

// Export parameters.
export {
	UseFormDisqualifiersParameters,
}

// Export results.
export {
	UseFormDisqualifiersResults,
}

export default useFormDisqualifiers;